/**
 * Copy propagation / scratch-temporary inlining for the lowered IR.
 *
 * At low optimization a compiler spills every subexpression to a fresh wasm local, so the
 * lowered body is a chain of single-use temporaries:
 *   var7 = index; var9 = var7; var11 = var9 < var10; ...
 * SSA lowering keeps these because they are wasm locals (its inliner refuses local-named copies,
 * since a wasm local can be meaningful storage). This pass collapses the chain by inlining each
 * GENERATED scratch temporary (`varN`) into its use, leaving `var11 = index < count`. Only
 * generated names are touched — DWARF/source names, loop counters, flags, and pointers keep
 * their identity, so naming quality is preserved while the noise is removed.
 *
 * Two safe rewrites, applied to a fixpoint:
 *   - constant propagation: a single-defined `varN = const` is replaced by the constant at every
 *     use (always safe — a constant never changes);
 *   - single-use forward substitution: a single-defined, single-used `varN = expr` is moved to
 *     its use when both are in the same straight-line block and nothing between them changes the
 *     value — no write to a variable it reads, and no store/call when it reads memory or a global.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';

const MAX_ITERATIONS = 16;

export function propagateCopies(node: LoweredNode): LoweredNode {
  let current = node;
  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
    const counts = countDefsAndUses(current);
    const counter = { changed: 0 };
    const afterConstants = propagateConstants(current, counts.defCount, counter);
    const afterCopies = forwardSubstitute(afterConstants, counts.defCount, counts.useCount, counter);
    current = afterCopies;
    if (counter.changed === 0) {
      break;
    }
  }
  return current;
}

// A generated scratch temporary, the only thing this pass dissolves. Source/DWARF names, loop
// counters, flags, pointers, and call-result names never match, so they are left untouched.
function isGeneratedTemporary(name: string): boolean {
  return /^var\d+$/.test(name);
}

// ─── constant propagation ───

function propagateConstants(node: LoweredNode, defCount: Map<string, number>, counter: { changed: number }): LoweredNode {
  const constants = new Map<string, Expression>();
  collectConstantDefinitions(node, defCount, constants);
  if (constants.size === 0) {
    return node;
  }
  counter.changed += constants.size;
  return rewriteWithConstants(node, constants);
}

function collectConstantDefinitions(node: LoweredNode, defCount: Map<string, number>, into: Map<string, Expression>): void {
  forEachStatement(node, statement => {
    if (statement.kind === 'assign' && statement.value.kind === 'const' &&
        isGeneratedTemporary(statement.target) && (defCount.get(statement.target) ?? 0) === 1) {
      into.set(statement.target, statement.value);
    }
  });
}

function rewriteWithConstants(node: LoweredNode, constants: Map<string, Expression>): LoweredNode {
  const replace = (expr: Expression): Expression => {
    if (expr.kind === 'var' && constants.has(expr.name)) {
      return constants.get(expr.name)!;
    }
    return rebuildExpression(expr, replace);
  };
  const keep = (statement: Statement): boolean =>
    !(statement.kind === 'assign' && constants.has(statement.target));
  return rebuildNode(node, statement => rebuildStatement(statement, replace), replace, keep);
}

// ─── single-use forward substitution ───

interface Substitution {
  expr: Expression;
  definition: Statement;
}

function forwardSubstitute(node: LoweredNode, defCount: Map<string, number>, useCount: Map<string, number>, counter: { changed: number }): LoweredNode {
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(child => forwardSubstitute(child, defCount, useCount, counter)) };
    case 'block': return { kind: 'block', body: substituteInBlock(node.body, defCount, useCount, counter) };
    case 'if': return { kind: 'if', condition: node.condition, thenBody: forwardSubstitute(node.thenBody, defCount, useCount, counter), elseBody: node.elseBody ? forwardSubstitute(node.elseBody, defCount, useCount, counter) : null };
    case 'while': return { kind: 'while', condition: node.condition, body: forwardSubstitute(node.body, defCount, useCount, counter) };
    case 'do_while': return { kind: 'do_while', body: forwardSubstitute(node.body, defCount, useCount, counter), condition: node.condition };
    case 'for': return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: forwardSubstitute(node.body, defCount, useCount, counter) };
    case 'switch': return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: forwardSubstitute(caseEntry.body, defCount, useCount, counter) })), defaultBody: forwardSubstitute(node.defaultBody, defCount, useCount, counter) };
    case 'labeled_block': return { kind: 'labeled_block', label: node.label, body: forwardSubstitute(node.body, defCount, useCount, counter) };
    default: return node;
  }
}

// One forward pass over a straight-line block. Available substitutions are applied at read
// sites (consuming the entry and dropping its definition), invalidated when their inputs change,
// and registered as each new single-use temporary is defined.
function substituteInBlock(body: Statement[], defCount: Map<string, number>, useCount: Map<string, number>, counter: { changed: number }): Statement[] {
  const available = new Map<string, Substitution>();
  const removed = new Set<Statement>();
  const rewritten: Statement[] = [];

  for (const original of body) {
    const apply = (expr: Expression): Expression => applySubstitutions(expr, available, removed, counter);
    const statement = rebuildStatement(original, apply);
    invalidateAffected(available, statement);
    registerIfTemporary(available, statement, defCount, useCount);
    rewritten.push(statement);
  }
  return rewritten.filter(statement => !removed.has(statement));
}

function applySubstitutions(expr: Expression, available: Map<string, Substitution>, removed: Set<Statement>, counter: { changed: number }): Expression {
  if (expr.kind === 'var') {
    const substitution = available.get(expr.name);
    if (substitution) {
      available.delete(expr.name);
      removed.add(substitution.definition);
      counter.changed++;
      return substitution.expr;
    }
    return expr;
  }
  return rebuildExpression(expr, child => applySubstitutions(child, available, removed, counter));
}

function invalidateAffected(available: Map<string, Substitution>, statement: Statement): void {
  const writtenLocal = assignedLocal(statement);
  if (writtenLocal !== null) {
    for (const [name, substitution] of available) {
      if (expressionReads(substitution.expr, writtenLocal)) {
        available.delete(name);
      }
    }
  }
  if (writesMemoryOrGlobal(statement)) {
    for (const [name, substitution] of available) {
      if (expressionIsImpure(substitution.expr)) {
        available.delete(name);
      }
    }
  }
}

function registerIfTemporary(available: Map<string, Substitution>, statement: Statement, defCount: Map<string, number>, useCount: Map<string, number>): void {
  if (statement.kind !== 'assign' || !isGeneratedTemporary(statement.target)) {
    return;
  }
  if ((defCount.get(statement.target) ?? 0) !== 1 || (useCount.get(statement.target) ?? 0) !== 1) {
    return;
  }
  // A self-referential or call-bearing value must not be moved: the former is ill-formed for a
  // single definition, the latter could duplicate or reorder a side effect.
  if (expressionReads(statement.value, statement.target) || expressionContainsCall(statement.value)) {
    return;
  }
  available.set(statement.target, { expr: statement.value, definition: statement });
}

// ─── statement / expression inspection ───

// The local a statement assigns (an assignment target or a call result), or null. A global_set
// targets a global, not a local, so it is handled by the memory/global invalidation instead.
function assignedLocal(statement: Statement): string | null {
  if (statement.kind === 'assign') {
    return statement.target;
  }
  if ((statement.kind === 'call' || statement.kind === 'call_indirect') && statement.result) {
    return statement.result;
  }
  return null;
}

function writesMemoryOrGlobal(statement: Statement): boolean {
  if (statement.kind === 'store' || statement.kind === 'call' || statement.kind === 'call_indirect' || statement.kind === 'global_set') {
    return true;
  }
  return statementExpressions(statement).some(expressionContainsCall);
}

function expressionReads(expr: Expression, name: string): boolean {
  if (expr.kind === 'var') {
    return expr.name === name;
  }
  return childExpressions(expr).some(child => expressionReads(child, name));
}

// True when the value depends on memory or a global (a load, a frame slot, a field access, a
// global read) or a call, so it cannot be moved across a write to memory / a global / another call.
function expressionIsImpure(expr: Expression): boolean {
  if (expr.kind === 'load' || expr.kind === 'frame_slot' || expr.kind === 'field_access' ||
      expr.kind === 'global' || expr.kind === 'call' || expr.kind === 'call_indirect') {
    return true;
  }
  return childExpressions(expr).some(expressionIsImpure);
}

function expressionContainsCall(expr: Expression): boolean {
  if (expr.kind === 'call' || expr.kind === 'call_indirect') {
    return true;
  }
  return childExpressions(expr).some(expressionContainsCall);
}

function countDefsAndUses(node: LoweredNode): { defCount: Map<string, number>; useCount: Map<string, number> } {
  const defCount = new Map<string, number>();
  const useCount = new Map<string, number>();
  const note = (counts: Map<string, number>, name: string): void => {
    counts.set(name, (counts.get(name) ?? 0) + 1);
  };
  const countReads = (expr: Expression): void => {
    if (expr.kind === 'var') {
      note(useCount, expr.name);
    }
    childExpressions(expr).forEach(countReads);
  };
  forEachStatement(node, statement => {
    const target = assignedLocal(statement);
    if (target !== null) {
      note(defCount, target);
    }
    statementExpressions(statement).forEach(countReads);
  });
  return { defCount, useCount };
}

// ─── generic lowered-tree walks ───

function forEachStatement(node: LoweredNode, visit: (statement: Statement) => void): void {
  switch (node.kind) {
    case 'sequence': node.children.forEach(child => forEachStatement(child, visit)); break;
    case 'block': node.body.forEach(visit); break;
    case 'if': forEachStatement(node.thenBody, visit); if (node.elseBody) { forEachStatement(node.elseBody, visit); } break;
    case 'while': case 'do_while': case 'labeled_block': forEachStatement(node.body, visit); break;
    case 'for': visit(node.init); visit(node.increment); forEachStatement(node.body, visit); break;
    case 'switch': node.cases.forEach(caseEntry => forEachStatement(caseEntry.body, visit)); forEachStatement(node.defaultBody, visit); break;
  }
}

function childExpressions(expr: Expression): Expression[] {
  switch (expr.kind) {
    case 'binary': case 'compare': return [expr.left, expr.right];
    case 'unary': case 'convert': return [expr.operand];
    case 'load': return [expr.address];
    case 'select': return [expr.condition, expr.trueVal, expr.falseVal];
    case 'call': return expr.args;
    case 'call_indirect': return [expr.tableIndex, ...expr.args];
    case 'field_access': return [expr.base];
    case 'frame_slot': return [expr.index];
    default: return [];
  }
}

function statementExpressions(statement: Statement): Expression[] {
  switch (statement.kind) {
    case 'assign': return [statement.value];
    case 'store': return [statement.address, statement.value];
    case 'call': return statement.args;
    case 'call_indirect': return [statement.tableIndex, ...statement.args];
    case 'global_set': return [statement.value];
    case 'return': return statement.values;
    case 'expr': return [statement.value];
    default: return [];
  }
}

function rebuildExpression(expr: Expression, map: (child: Expression) => Expression): Expression {
  switch (expr.kind) {
    case 'binary': return { kind: 'binary', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'compare': return { kind: 'compare', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'unary': return { kind: 'unary', op: expr.op, operand: map(expr.operand) };
    case 'convert': return { kind: 'convert', op: expr.op, operand: map(expr.operand) };
    case 'select': return { kind: 'select', condition: map(expr.condition), trueVal: map(expr.trueVal), falseVal: map(expr.falseVal) };
    case 'load': return { kind: 'load', address: map(expr.address), offset: expr.offset, loadType: expr.loadType };
    case 'call': return { kind: 'call', name: expr.name, args: expr.args.map(map) };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: map(expr.tableIndex), args: expr.args.map(map) };
    case 'field_access': return { kind: 'field_access', base: map(expr.base), offset: expr.offset };
    case 'frame_slot': return { kind: 'frame_slot', index: map(expr.index) };
    default: return expr;
  }
}

function rebuildStatement(statement: Statement, map: (expr: Expression) => Expression): Statement {
  switch (statement.kind) {
    case 'assign': return { kind: 'assign', target: statement.target, type: statement.type, value: map(statement.value) };
    case 'store': return { kind: 'store', address: map(statement.address), offset: statement.offset, storeType: statement.storeType, value: map(statement.value) };
    case 'call': return { kind: 'call', name: statement.name, args: statement.args.map(map), result: statement.result };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: map(statement.tableIndex), args: statement.args.map(map), result: statement.result };
    case 'global_set': return { kind: 'global_set', name: statement.name, value: map(statement.value) };
    case 'return': return { kind: 'return', values: statement.values.map(map) };
    case 'expr': return { kind: 'expr', value: map(statement.value) };
    default: return statement;
  }
}

function rebuildNode(
  node: LoweredNode,
  mapStatement: (statement: Statement) => Statement,
  mapExpression: (expr: Expression) => Expression,
  keepStatement: (statement: Statement) => boolean,
): LoweredNode {
  const recurse = (child: LoweredNode): LoweredNode => rebuildNode(child, mapStatement, mapExpression, keepStatement);
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(recurse) };
    case 'block': return { kind: 'block', body: node.body.filter(keepStatement).map(mapStatement) };
    case 'if': return { kind: 'if', condition: mapExpression(node.condition), thenBody: recurse(node.thenBody), elseBody: node.elseBody ? recurse(node.elseBody) : null };
    case 'while': return { kind: 'while', condition: node.condition ? mapExpression(node.condition) : null, body: recurse(node.body) };
    case 'do_while': return { kind: 'do_while', body: recurse(node.body), condition: mapExpression(node.condition) };
    case 'for': return { kind: 'for', init: mapStatement(node.init), condition: mapExpression(node.condition), increment: mapStatement(node.increment), body: recurse(node.body) };
    case 'switch': return { kind: 'switch', selector: mapExpression(node.selector), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: recurse(caseEntry.body) })), defaultBody: recurse(node.defaultBody) };
    case 'labeled_block': return { kind: 'labeled_block', label: node.label, body: recurse(node.body) };
    case 'return': return { kind: 'return', values: node.values.map(mapExpression) };
    default: return node;
  }
}
